import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { usePatientRequestContext } from '../../../context/usePatientRequestContext';
import { RequestSelection } from './RequestSelection';
import { RequestActionsDetails } from './RequestActionsDetails';
import { RequestSubStep } from '../../../types';

const getInitialSubStep = (isEditMode: boolean): RequestSubStep =>
  isEditMode ? 'actions-details' : 'actions-selection';

const setSubStepInUrl = (
  setSearchParams: (
    params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    options?: { replace: boolean },
  ) => void,
  nextSubStep: RequestSubStep,
  opts?: { replace?: boolean },
) => {
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev);
      next.set('substep', nextSubStep);
      return next;
    },
    { replace: opts?.replace ?? false },
  );
};

export const RequestSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentStepIndex, setCurrentStep, isEditMode } =
    usePatientRequestContext();

  const [currentSubStep, setCurrentSubStep] = useState<RequestSubStep>(() =>
    getInitialSubStep(isEditMode),
  );

  // Check if accessed with patientId (existing patient flow)
  const hasPatientId = searchParams.has('patientId');
  const shouldDisablePrevious = hasPatientId;

  // Ensure URL includes 'actions-details' substep in edit mode, and keep UI in sync with back/forward.
  useEffect(() => {
    if (!isEditMode) return;

    const urlSubStep = searchParams.get('substep') as RequestSubStep | null;
    const targetSubStep: RequestSubStep = 'actions-details';

    // Sync URL to target substep if missing or mismatched
    if (urlSubStep !== targetSubStep) {
      setSubStepInUrl(setSearchParams, targetSubStep, { replace: true });
      setCurrentSubStep(targetSubStep);
      return;
    }

    // Sync local state with URL (handles browser back/forward)
    if (currentSubStep !== urlSubStep) {
      setCurrentSubStep(urlSubStep);
    }
  }, [currentSubStep, isEditMode, searchParams, setSearchParams]);

  const handleNextSubStep = () => {
    setCurrentSubStep('actions-details');
  };

  const handlePreviousSubStep = () => {
    setCurrentSubStep('actions-selection');
  };

  const handleCompleteRequest = () => {
    setCurrentStep(currentStepIndex + 1);
  };

  const handleBackToMainStep = () => {
    setCurrentStep(currentStepIndex - 1);
  };

  return (
    <>
      {!isEditMode && currentSubStep === 'actions-selection' && (
        <RequestSelection
          onNext={handleNextSubStep}
          onBackToMainStep={handleBackToMainStep}
          disablePrevious={shouldDisablePrevious}
        />
      )}
      {(isEditMode || currentSubStep === 'actions-details') && (
        <RequestActionsDetails
          onPrevious={isEditMode ? handleBackToMainStep : handlePreviousSubStep}
          disablePrevious={isEditMode}
          onComplete={handleCompleteRequest}
        />
      )}
    </>
  );
};
